import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { isMobile, isTablet } from 'react-device-detect';
import Chat from '../../pages/Chat/Chat';
import ActivityLibrary from '../../pages/ActivityLibrary/ActivityLibrary';
import Clients from '../../pages/Clients/Clients';
import Settings from '../../pages/Settings/Settings';
import Client from '../../pages/SingleClient/SingleClient';
import Notification from '../../pages/Notification/Notification';
import {
  ACTIVITY_LIBRARY_ROUTE,
  ADD_NEW_APPOINTMENT_ROUTE,
  APPOINTMENTS_ROUTE,
  CHAT_ROUTE,
  CLIENTS_ROUTE,
  CLIENT_ROUTE,
  SETTINGS_ROUTE,
  NOTIFICATION_ROUTE,
  LOGIN_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  COACHES_ROUTE,
  APPLICATIONS_ROUTE,
  COACH_ONBOARDING_ROUTE,
  HOME_ROUTE,
  ADD_NEW_COACH_ROUTE,
  ONBOARDING_ROUTE,
  ARCHIVED_CLIENTS_ROUTE,
  ARCHIVED_COACHES_ROUTE,
  EMAIL_VERIFICATION_ROUTE,
  COACH_SIGNUP_WITH_EMAIL_ROUTE,
  SIGNUP_ROUTE,
  USER_GROUPS_ROUTE,
  PAYMENT_INFO_ROUTE,
  PAYMENT_SUCCESS_ROUTE,
  CHECKOUT_PAGE_ROUTE,
  PAYMENT_SETTINGS_ROUTE,
  VIEW_BOOKINGS_ROUTE,
  AFFILIATE_SIGNUP_ROUTE,
  AFFILIATE_LOGIN_ROUTE,
  AFFILIATE_COACH_SIGNUP_WITH_EMAIL_ROUTE,
  AFFILIATE_EMAIL_VERIFICATION_ROUTE,
  AFFILIATE_FORGOT_PASSWORD_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  COACH_PROFILE_ROUTE,
  NOT_FOUND_ROUTE,
  SUBSCRIPTION_PLANS,
  COACH_BOOKING_CONFIRMATION,
  COACH_PROFILE_SET_BOOKING_DATE_ROUTE,
  COACH_PROFILE_SET_BOOKING_TIME_ROUTE,
  COACH_PROFILE_SET_BOOKING_DETAILS_ROUTE,
  MANAGE_TEAM_ROUTE,
  CREATE_PASSWORD_ROUTE,
  TEAM_CLIENTS_ROUTE,
} from '../../utils/routes';
import AddAppointmentModal from '../AddAppointmentModal/AddAppointmentModal';
import PrivateRoute from '../PrivateRoute/PrivateRoute';
import Login from '../Login/Login';
import ForgetPassword from '../../pages/ForgetPassword/ForgetPassword';
import Coaches from '../../pages/Coaches/Coaches';
import Applications from '../../pages/Applications/Applications';
import AddCoach from '../AddNewCoach/AddNewCoach';
import CoachOnboarding from '../../pages/CoachOnboarding/CoachOnboading';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { UserType } from '../User/User';
import Onboarding from '../../pages/Onboarding/Onboarding';
import ArchivedClientsView from '../../pages/Clients/ArchivedClients';
import CoachPageLayout from '../CoachPageLayout/CoachPageLayout';
import ArchivedCoachesView from '../../pages/Coaches/ArchivedCoaches';
import EmailVerification from '../Onboarding/EmailVerification';
import OnboardingPagesLayout from '../Layout/OnboardingLayout';
import Details from '../Onboarding/Details';
import SignUp from '../../pages/SignUp/SignUp';
import UserGroups from '../../pages/UserGroups/UserGroups';
import NotFound from '../NotFound/NotFound';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import PaymentInfo from '../../pages/PaymentInfo/PayementInfo';
import SubscriptionSuccess from '../../pages/SubscriptionSuccess/SubscriptionSuccess';
import Checkout from '../CheckOut/CheckOut';
import PaymentSettings from '../../pages/PaymentSettings/PaymentSettings';
import ViewBookings from '../../pages/Bookings/ViewBookings';
import MobileScreen from '../../pages/MobileScreen/MobileScreen';
import BookingSettings from '../../pages/BookingSettings/BookingSettings';
import MiniWebSite from '../../pages/MiniWebSite/MiniWebSite';
import SubscriptionPlanManage from '../../pages/SubscriptionPlanManage/SubscriptionPlanManage';
import BookingConfirmation from '../../pages/MiniWebSite/BookingConfirmation/BookingConfirmation';
import MiniWebSiteSetBookingDate from '../../pages/MiniWebSite/MiniWebSiteSetBookingDate';
import MiniWebSiteSetBookingDetails from '../../pages/MiniWebSite/MiniWebSiteSetBookingDetails';
import MiniWebSiteSetBookingTime from '../../pages/MiniWebSite/MiniWebSiteSetBookingTime';
import { fetchCoachConfigurations } from '../../redux/booking/bookingSlice';
import MiniWebSiteNotFoundPage from '../../pages/MiniWebSite/MiniWebSiteNotFoundPage';
import TeamMembers from '../../pages/Team/TeamMembers';
import { resetState } from '../../redux/GlobalAction';
import CreatePassword from '../../pages/CreatePassword/CreatePassword';
import TeamMemberClients from '../TeamMembersTable/TeamMemberClients/TeamMemberClients';

const RootRoute = () => {
  const userType = useAppSelector((state) => state.coach.role);
  const permissionCheckers = createPermissionCheckers();
  const { withPermissions, checkPermissions } = permissionCheckers;
  const dispatch = useAppDispatch();
  const coach = useAppSelector((state) => state.coach.coach);

  useEffect(() => {
    if (!coach.id) {
      dispatch(resetState());
      return;
    }
    if (!checkPermissions(UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS)) {
      return;
    }
    if (!checkPermissions(UserPermissionType.BOOKINGS_MODULE_VIEW_BOOKINGS)) {
      return;
    }
    dispatch(fetchCoachConfigurations({ coachId: coach.id }));
  }, [coach.id]);

  if (isMobile && !isTablet) {
    return (
      <Router>
        <Routes>
          <Route path={CREATE_PASSWORD_ROUTE} element={<CreatePassword />} />
          <Route path={LOGIN_ROUTE} element={<Login />} />
          <Route path={AFFILIATE_LOGIN_ROUTE} element={<Login />} />
          <Route path={SIGNUP_ROUTE} element={<SignUp />} />
          <Route path={AFFILIATE_SIGNUP_ROUTE} element={<SignUp />} />
          <Route path={FORGOT_PASSWORD_ROUTE} element={<ForgetPassword />} />
          <Route path={AFFILIATE_FORGOT_PASSWORD_ROUTE} element={<ForgetPassword />} />
          <Route path={COACH_ONBOARDING_ROUTE} element={<CoachOnboarding />} />
          <Route
            path={ONBOARDING_ROUTE}
            element={
              <PrivateRoute>
                <Onboarding />
              </PrivateRoute>
            }
          />
          <Route
            path={COACH_SIGNUP_WITH_EMAIL_ROUTE}
            element={
              <OnboardingPagesLayout xs={11} md={10} lg={7}>
                <Details />
              </OnboardingPagesLayout>
            }
          />
          <Route
            path={AFFILIATE_COACH_SIGNUP_WITH_EMAIL_ROUTE}
            element={
              <OnboardingPagesLayout xs={11} md={10} lg={7}>
                <Details />
              </OnboardingPagesLayout>
            }
          />
          <Route
            path={EMAIL_VERIFICATION_ROUTE}
            element={
              <OnboardingPagesLayout>
                <EmailVerification />
              </OnboardingPagesLayout>
            }
          />
          <Route path={COACH_BOOKING_CONFIRMATION} element={<BookingConfirmation />} />
          <Route path="*" element={<MobileScreen />} />
          <Route path={COACH_PROFILE_ROUTE} element={<MiniWebSite />} />
          <Route
            path={COACH_PROFILE_SET_BOOKING_DATE_ROUTE}
            element={<MiniWebSiteSetBookingDate />}
          />
          <Route
            path={COACH_PROFILE_SET_BOOKING_TIME_ROUTE}
            element={<MiniWebSiteSetBookingTime />}
          />
          <Route
            path={COACH_PROFILE_SET_BOOKING_DETAILS_ROUTE}
            element={<MiniWebSiteSetBookingDetails />}
          />
          <Route path={NOT_FOUND_ROUTE} element={<MiniWebSiteNotFoundPage />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        {userType === UserType.COACH && (
          <Route path={HOME_ROUTE} element={<Navigate to={CLIENTS_ROUTE} replace />} />
        )}
        {userType === UserType.ADMIN && (
          <Route path={HOME_ROUTE} element={<Navigate to={COACHES_ROUTE} replace />} />
        )}
        <Route
          path={APPOINTMENTS_ROUTE}
          element={
            <PrivateRoute>
              <Clients />
            </PrivateRoute>
          }
        />
        <Route
          path={CHAT_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <Chat />,
                permissions: [UserPermissionType.CHAT_MODULE_VIEW_CHAT],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={ACTIVITY_LIBRARY_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: (
                  <CoachPageLayout>
                    <ActivityLibrary />
                  </CoachPageLayout>
                ),
                permissions: [UserPermissionType.ACTIVITY_LIBRARY_MODULE_GET_ACTIVITY_LIBRARY],
                checkAll: false,
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={CLIENTS_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <Clients />,
                permissions: [UserPermissionType.CORE_MODULE_GET_CLIENTS],
                checkAll: false,
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={CLIENT_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <Client />,
                permissions: [
                  UserPermissionType.CORE_MODULE_VIEW_SINGLE_CLIENT,
                  UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT,
                  UserPermissionType.NOTE_MODULE_VIEW_NOTES,
                  UserPermissionType.PROGRESS_MODULE_GET_PROGRESS_SUMMARY,
                  UserPermissionType.ACTIVITY_MODULE_VIEW_ACTIVITIES,
                ],
                checkAll: false,
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={ARCHIVED_CLIENTS_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <ArchivedClientsView />,
                permissions: [UserPermissionType.CORE_MODULE_VIEW_ARCHIVE_CLIENTS],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={SETTINGS_ROUTE}
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />
        <Route
          path={ADD_NEW_COACH_ROUTE}
          element={
            <PrivateRoute>
              <AddCoach />
            </PrivateRoute>
          }
        />
        <Route
          path={ADD_NEW_APPOINTMENT_ROUTE}
          element={
            <PrivateRoute>
              <AddAppointmentModal />
            </PrivateRoute>
          }
        />
        <Route
          path={NOTIFICATION_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <Notification />,
                permissions: [UserPermissionType.NOTIFICATION_MODULE_VIEW_NOTIFICATIONS],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={COACHES_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <Coaches />,
                permissions: [UserPermissionType.COACH_MODULE_VIEW_ACTIVE_COACHES],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={ARCHIVED_COACHES_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <ArchivedCoachesView />,
                permissions: [UserPermissionType.COACH_MODULE_VIEW_ARCHIVE_COACHES],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={APPLICATIONS_ROUTE}
          element={
            <PrivateRoute>
              <Applications />
            </PrivateRoute>
          }
        />
        <Route
          path={USER_GROUPS_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <UserGroups />,
                permissions: [UserPermissionType.CORE_MODULE_VIEW_USER_GROUPS],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={PAYMENT_INFO_ROUTE}
          element={
            <PrivateRoute>
              <PaymentInfo />
            </PrivateRoute>
          }
        />
        <Route path={PAYMENT_SUCCESS_ROUTE} element={<SubscriptionSuccess />} />
        <Route path={FORGOT_PASSWORD_ROUTE} element={<ForgetPassword />} />
        <Route path={AFFILIATE_FORGOT_PASSWORD_ROUTE} element={<ForgetPassword />} />
        <Route
          path={ONBOARDING_ROUTE}
          element={
            <PrivateRoute>
              <Onboarding />
            </PrivateRoute>
          }
        />
        <Route
          path={COACH_SIGNUP_WITH_EMAIL_ROUTE}
          element={
            <OnboardingPagesLayout xs={11} md={10} lg={7}>
              <Details />
            </OnboardingPagesLayout>
          }
        />
        <Route
          path={AFFILIATE_COACH_SIGNUP_WITH_EMAIL_ROUTE}
          element={
            <OnboardingPagesLayout xs={11} md={10} lg={7}>
              <Details />
            </OnboardingPagesLayout>
          }
        />
        <Route
          path={EMAIL_VERIFICATION_ROUTE}
          element={
            <OnboardingPagesLayout>
              <EmailVerification />
            </OnboardingPagesLayout>
          }
        />
        <Route
          path={AFFILIATE_EMAIL_VERIFICATION_ROUTE}
          element={
            <OnboardingPagesLayout>
              <EmailVerification />
            </OnboardingPagesLayout>
          }
        />
        <Route
          path={PAYMENT_SETTINGS_ROUTE}
          element={
            <PrivateRoute>
              <PaymentSettings />
            </PrivateRoute>
          }
        />
        <Route path={SIGNUP_ROUTE} element={<SignUp />} />
        <Route path={AFFILIATE_SIGNUP_ROUTE} element={<SignUp />} />
        <Route path={PAYMENT_INFO_ROUTE} element={<PaymentInfo />} />
        <Route path={PAYMENT_SUCCESS_ROUTE} element={<SubscriptionSuccess />} />
        <Route path={CHECKOUT_PAGE_ROUTE} element={<Checkout />} />

        <Route
          path={VIEW_BOOKINGS_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <ViewBookings />,
                permissions: [
                  UserPermissionType.BOOKINGS_MODULE_VIEW_BOOKINGS,
                  UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS,
                ],
                checkAll: true,
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={CALENDAR_SETTINGS_ROUTE}
          element={
            <PrivateRoute>
              {withPermissions({
                children: <BookingSettings />,
                permissions: [UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS],
                placeholder: <NotFound />,
              })}
            </PrivateRoute>
          }
        />
        <Route
          path={CHECKOUT_PAGE_ROUTE}
          element={
            <PrivateRoute>
              <Checkout />
            </PrivateRoute>
          }
        />
        <Route
          path={SUBSCRIPTION_PLANS}
          element={
            <PrivateRoute>
              <SubscriptionPlanManage />
            </PrivateRoute>
          }
        />
        <Route
          path={VIEW_BOOKINGS_ROUTE}
          element={
            <PrivateRoute>
              <ViewBookings />
            </PrivateRoute>
          }
        />
        <Route path={CREATE_PASSWORD_ROUTE} element={<CreatePassword />} />
        <Route path={COACH_ONBOARDING_ROUTE} element={<CoachOnboarding />} />
        <Route path={LOGIN_ROUTE} element={<Login />} />
        <Route path={AFFILIATE_LOGIN_ROUTE} element={<Login />} />
        <Route path={COACH_PROFILE_ROUTE} element={<MiniWebSite />} />
        <Route path={COACH_BOOKING_CONFIRMATION} element={<BookingConfirmation />} />
        <Route path={NOT_FOUND_ROUTE} element={<MiniWebSiteNotFoundPage />} />
        {/* Catch-all route for unknown URLs */}
        <Route path="*" element={<Navigate to={NOT_FOUND_ROUTE} replace />} />
        <Route
          path={COACH_PROFILE_SET_BOOKING_DATE_ROUTE}
          element={<MiniWebSiteSetBookingDate />}
        />
        <Route
          path={COACH_PROFILE_SET_BOOKING_TIME_ROUTE}
          element={<MiniWebSiteSetBookingTime />}
        />
        <Route
          path={COACH_PROFILE_SET_BOOKING_DETAILS_ROUTE}
          element={<MiniWebSiteSetBookingDetails />}
        />
        <Route
          path={MANAGE_TEAM_ROUTE}
          element={
            <PrivateRoute>
              <TeamMembers />
            </PrivateRoute>
          }
        />
        <Route
          path={TEAM_CLIENTS_ROUTE}
          element={
            <PrivateRoute>
              <TeamMemberClients />
            </PrivateRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default RootRoute;
